import axios, { AxiosResponse } from "axios";
import { Retiree } from "../retiree";
import { PensionPot } from "../pensionPot";
import { FinalSalary } from "../finalSalary";
import Constants from "../constants";
import { Saving } from "../saving";
import { Scenario } from "../scenario";
import ObjectBuilder from "../objectBuilder";
import ScenarioType from "../scenarioType";
import ScenarioOutcome from "../scenarioOutcome";
import { OtherCash } from "../otherCash";
import { OtherIncome } from "../otherIncome";
import { StatePensionDate } from "../statePensionDate";
import TakeLessResponse from "../solve/takeLessResponse";
import PayMoreResponse from "../solve/payMoreResponse";
import { formatter } from "../../../shared/ts/formatter";
import { CanBuyGuaranteedIncomeResponse } from "../response/canBuyGuaranteedIncomeResponse";
import { MessageResponse } from "../response/messageResponse";
import HttpStatusCode from "../../../shared/ts/httpStatusCode";
import ScenarioInfo from "../scenarioInfo";
import Lifestyle from "../lifestyle";
import { SubscribeToGuaranteedIncomeResponse } from "../response/subscribeToGuaranteedIncomeResponse";
import HealthState from "../healthState";
import { AccountSearchResponse } from "../response/accountSearchResponse";
import User from "../user";
import { AccountSearchResponseItem } from "../response/accountSearchResponseItem";
import { apiState } from "./apiState";
import { FinishInfoResponse } from "../response/finishInfoResponse";
import { getRequestContext } from "./apiUtilities";
import EquityReleaseResponse from "../solve/equityReleaseResponse";
import { EquityReleaseRateResponse } from "../response/equityReleaseRateResponse";
import { BeginPensionTransferResponse } from "../response/beginPensionTransferResponse";
import { AnnuityProvidersResponse } from "../response/annuityProvidersResponse";
import { AdvisersResponse } from "../response/advisersResponse";
import PermittedEntityType from "../consent/permittedEntityType";
import ExternalAgency from "../consent/externalAgency";
import ConsentType from "../consent/consentType";
import { AdditionalPersonalInfo } from "../additionalPersonalInfo";
import DataResult from "../dataResult";
import { Country } from "../country";
import { PensionProvidersResponse } from "../response/pensionProvidersResponse";
import { ApplicationInfo } from "../secure/applicationInfo";
import FundAllocation from "../fundAllocation";
import FinalSalaryOption from "../assets/finalsalary/finalSalaryOption";
import { Notification as AppNotification } from "../notifications/notification";
import NotificationConcern from "../notifications/notificationConcern";
import FinalSalarySchemeOptionsDetailResponse from "../response/finalSalarySchemeOptionsDetailResponse";
import { RedirectResponse } from "../response/redirectResponse";
import dateTimeProvider from "../systemDateTimeProvider";

class API {
  public async transferToPensionLab(returnView: string) {
    const resource = `/retiree/{retireeId}/pensionpots/livevalues/transfer?currentView=${returnView}`;
    return await axios.get(resource);
  }

  public async getUser(isImpersonating: boolean, userStorageActor: (user: User) => void): Promise<User | null> {
    try {
      const responseForLoggedInUser = await axios.get("/user");
      const loggedInUser = new User(responseForLoggedInUser.data);

      if (isImpersonating) {
        const responseForImpersonatedUser = await axios.get("/user/retiree/{retireeId}");
        const impersonatedUser = new User(responseForImpersonatedUser.data);
        loggedInUser.systemDate = impersonatedUser.systemDate;
      }

      userStorageActor(loggedInUser);
      apiState.isInitialCallCompleted = true;
      apiState.initialCallPromiseResolver!();

      return loggedInUser;
    } catch (error) {
      console.log("api.getUser:catch", error);
      return null;
    }
  }

  public async getRetiree(): Promise<AxiosResponse> {
    const resource = `/retiree/{retireeId}`;
    return await axios.get(resource);
  }

  public async getProviderCharges(): Promise<AxiosResponse> {
    const resource = `/retiree/{retireeId}/providers/charges`;
    return await axios.get(resource);
  }

  public async getRetirementPlan(): Promise<AxiosResponse> {
    return await axios.get(`/retiree/{retireeId}/plan`);
  }

  public downloadReport(): void {
    const requestContext = getRequestContext();
    const url = requestContext.verifyUrl("retiree/{retireeId}/reports/plan");
    window.open(url, '_self');
  }

  public async getConfig(): Promise<AxiosResponse> {
    return await axios.get("/theme/config");
  }

  public async updatePersonalDetail(dateOfBirth: Date, sex: string, alreadyDrawingPension: boolean, retirementDate: Date): Promise<MessageResponse> {
    try {
      const sexChar = sex === "Male" ? "M" : "F";

      const payload = {
        dateOfBirth: formatter.standardDate(dateOfBirth),
        sex: sexChar,
        retirementDate: formatter.standardDate(retirementDate),
        alreadyDrawingPension: alreadyDrawingPension
      };

      const { data } = await axios.patch("/personaldetails/patch", payload);

      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";
        return Promise.resolve<MessageResponse>(new MessageResponse(status, message));
      } else {
        return Promise.resolve<MessageResponse>(new MessageResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred."));
      }
    }
  }

  public async runScenario(
    scenarioType: ScenarioType,
    withGuaranteedIncome: boolean
  ): Promise<Scenario> {
    const resource = `/retiree/{retireeId}/test`;

    const useFinishedDesign = true;
    const scenarioName = ScenarioInfo.getScenarioServerName(scenarioType);
    const config = { params: { scenarioName, useFinishedDesign, withGuaranteedIncome } };
    let scenario: Scenario | null = null;

    await axios.get(resource, config).then(r => {
      const scenarioOutcome: ScenarioOutcome = r.data.Data.HasDeficit
        ? ScenarioOutcome.Fail
        : ScenarioOutcome.Pass;
      scenario = ObjectBuilder.createScenario(scenarioType, scenarioOutcome);
    });

    if (scenario == null) {
      throw new Error('Scenario not recovered.');
    }

    return scenario;
  }

  public async runAllTestScenariosForGuaranteedIncome(): Promise<
    Array<Scenario>
  > {
    let result = Array<Scenario>();

    await Promise.all([
      this.runScenario(ScenarioType.LowerGrowth, true),
      this.runScenario(ScenarioType.LiveThreeYearsLonger, true),
      this.runScenario(ScenarioType.PotsFallTwentyPercent, true),
      this.runScenario(ScenarioType.NeedExtraMoneyForLastTwoYears, true)
    ]).then(responses => {
      result = responses;
    });

    return result;
  }

  public async getChartData(): Promise<any> {
    const resource = `/retiree/{retireeId}/chartdata`;
    let graphData: any = null;

    await axios.get(resource).then(response => {
      graphData = response.data as Retiree;
    });

    return graphData;
  }

  public async getFinishInfo(): Promise<FinishInfoResponse | null> {
    try {
      const response = await axios.get(`/retiree/{retireeId}/finishinfo`);
      const hasTaxLiability = response.data.hasTaxLiability;
      const planTaxSaving = response.data.planTaxSaving;
      const providerCharges = response.data.providerCharges;
      return new FinishInfoResponse(hasTaxLiability, planTaxSaving, providerCharges);
    } catch (error) {
      return null;
    }
  }

  public async getStatePensionTemporal(
    dateOfBirth: Date,
    sex: string
  ): Promise<StatePensionDate> {
    const formattedDoB = formatter.standardDate(dateOfBirth);
    const resource = `/personaldetails/statepensiondetail?dateOfBirth=${formattedDoB}&sex=${sex}`;

    const statePensionDate: StatePensionDate = {
      StatePensionDate: dateTimeProvider.now(),
      SPA: 1
    };

    await axios.get(resource).then(response => {
      statePensionDate.StatePensionDate = new Date(
        response.data.StatePensionDate
      );
      statePensionDate.SPA = response.data.SPA;
    });

    return statePensionDate;
  }

  public async updateRequirements(
    takingDCTaxFreeAsLumpSum: boolean,
    livesInLondon: boolean,
    supportingPartner: boolean,
    lifestyle: Lifestyle,
    afterTaxIncome: number,
    extraIncome: number,
    increaseIncomeWithInflation: boolean,
    increaseIncomeWithInflationDuration: string,
    income: Array<number>): Promise<MessageResponse> {

    const payload = {
      takingDCTaxFreeAsLumpSum,
      livesInLondon,
      supportingPartner,
      lifestyle,
      afterTaxIncome,
      extraIncome,
      increaseIncomeWithInflation,
      increaseIncomeWithInflationDuration,
      income
    };

    try {
      const { data } = await axios.patch<MessageResponse>(
        "requirements/patch",
        payload
      );
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      const messageResponse = this.getMessageResponseFromError(error);
      return Promise.resolve<MessageResponse>(messageResponse)
    }
  }

  public async updateStatePension(amount: number): Promise<MessageResponse> {
    try {
      const { data } = await axios.post<MessageResponse>(
        "statepension/amount",
        { amount }
      );
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      const messageResponse = this.getMessageResponseFromError(error);
      return Promise.resolve<MessageResponse>(messageResponse)
    }
  }

  public async refineStatePension(
    amount: number,
    pastContributionYears: number,
    futureContributionYears: number,
    copeDeduction: number
  ): Promise<MessageResponse> {
    try {
      const { data } = await axios.post<MessageResponse>(
        "statepension/refine",
        {
          amount,
          pastContributionYears,
          futureContributionYears,
          copeDeduction
        }
      );
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      const messageResponse = this.getMessageResponseFromError(error);
      return Promise.resolve<MessageResponse>(messageResponse)
    }
  }

  public async undoRefineStatePension(): Promise<MessageResponse> {
    try {
      const { data } = await axios.post<MessageResponse>(
        "statepension/undorefine"
      );
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      const messageResponse = this.getMessageResponseFromError(error);
      return Promise.resolve<MessageResponse>(messageResponse)
    }
  }

  public async updatePensionPot(
    pensionPot: PensionPot
  ): Promise<AxiosResponse> {
    const action = pensionPot.id === Constants.NewId ? "create" : "edit";
    const resource = `/definedcontribution/${action}`;
    const payload = {
      id: pensionPot.id,
      providerName: pensionPot.name,
      valuation: pensionPot.valuationAmount,
      isPayingContributionsToTgtRetDate: pensionPot.isPayingContributionsToTgtRetDate,
      willTheseIncreaseIfSalaryIncreases: pensionPot.willTheseIncreaseIfSalaryIncreases,
      personalMonthlyContribution: pensionPot.personalMonthlyContribution,
      employerMonthlyContribution: pensionPot.employerMonthlyContribution
    };
    return axios.post(resource, payload);
  }

  public async updateFinalSalary(
    finalSalary: FinalSalary
  ): Promise<AxiosResponse> {
    const resource = this.buildResource("finalsalary", finalSalary.id);

    const formData = new FormData();
    formData.append("key", "value");
    formData.append("finalSalaryModel[ID]", finalSalary.id.toString());
    formData.append("finalSalaryModel[HasCashAndPensionQuote]", "true");
    formData.append("finalSalaryModel[Name]", finalSalary.name);
    formData.append("finalSalaryModel[IsPayableForLife]", "true");
    formData.append("finalSalaryModel[IsInPayment]", "false");
    formData.append(
      "finalSalaryModel[PlannedDateToTakeBenefits.CaptureDayOfMonth]",
      "false"
    );
    formData.append(
      "finalSalaryModel[PlannedDateToTakeBenefits.Month.SelectedItem]",
      finalSalary.payableFromMonth.toString()
    );
    formData.append(
      "finalSalaryModel[PlannedDateToTakeBenefits.Year.SelectedItem]",
      finalSalary.payableFromYear.toString()
    );
    formData.append("finalSalaryTrancheModelList[0][ID]", "1");
    formData.append("finalSalaryTrancheModelList[0][FinalSalaryId]", "0");
    formData.append("finalSalaryTrancheModelList[0][Name]", "Part 1");
    formData.append(
      "finalSalaryTrancheModelList[0][Amount]",
      finalSalary.startingIncome.toString()
    );
    formData.append(
      "finalSalaryTrancheModelList[0][InflationOptions]",
      finalSalary.inflation
    );
    formData.append("finalSalaryTrancheModelList[0][Description]", "");

    return axios.post(resource, formData);
  }

  public async updateSaving(saving: Saving): Promise<AxiosResponse> {
    const resource = this.buildResource("OtherSavings", saving.id);

    const formData = new FormData();
    formData.append("ID", saving.id.toString());
    formData.append("Name", saving.name);
    formData.append(
      "ValuationDate.HiddenDate",
      dateTimeProvider.now().toLocaleDateString("en-GB")
    );
    formData.append("AssummedReturn", "0");
    formData.append("Valuation", saving.currentValue.toString());
    formData.append("IsStillPaying", saving.isStillPaying.toString());
    formData.append("MonthlyContribution", (saving.monthlyPayment ?? 0).toString());
    formData.append(
      "WillTheseIncreaseIfSalaryIncreases",
      saving.willPaymentsIncrease.toString()
    );

    return axios.post(resource, formData);
  }

  public async updateOtherCash(otherCash: OtherCash) {
    const resource = this.buildResource("otherinvestments", otherCash.id);

    const formData = new FormData();
    formData.append("ID", otherCash.id.toString());
    formData.append("Name", otherCash.name);
    formData.append("ValuationDate.HiddenDate", dateTimeProvider.now().toLocaleDateString("en-GB"));
    formData.append("AssumedReturn", "0");
    formData.append("TaxRateOnMaturityValue", "0");
    formData.append("IsTakingAnIncomeBeforeMaturityDate", "False");
    formData.append("PercentageOfValueTakenAsIncome", "0");
    formData.append("IsTaxable", "False");
    formData.append("ValueAtValuationDate", otherCash.amount.toString());
    formData.append("MaturityOrSaleDate.CaptureDayOfMonth", "False");
    formData.append(
      "MaturityOrSaleDate.Month.SelectedItem",
      otherCash.payableFromMonth.toString()
    );
    formData.append(
      "MaturityOrSaleDate.Year.SelectedItem",
      otherCash.payableFromYear.toString()
    );

    return axios.post(resource, formData);
  }

  public async updateOtherIncome(otherIncome: OtherIncome) {
    const resource = this.buildResource("otherIncome", otherIncome.id);

    const formData = new FormData();
    formData.append("ID", otherIncome.id.toString());
    formData.append("Name", otherIncome.name);
    formData.append("OtherIncomeType", "Annual");
    formData.append("AnnualIncomeIncreaseIsFixedAmount", "False");
    formData.append("FixedRate", "0");
    formData.append("IsPayableForLife", "False");
    formData.append("Taxable", "True");
    formData.append("Amount", otherIncome.amount.toString());
    formData.append("PayableFromDate.CaptureDayOfMonth", "False");
    formData.append(
      "PayableFromDate.Month.SelectedItem",
      otherIncome.payableFromMonth.toString()
    );
    formData.append(
      "PayableFromDate.Year.SelectedItem",
      otherIncome.payableFromYear.toString()
    );
    formData.append("PayableToDate.CaptureDayOfMonth", "False");
    formData.append(
      "PayableToDate.Month.SelectedItem",
      otherIncome.payableToMonth.toString()
    );
    formData.append(
      "PayableToDate.Year.SelectedItem",
      otherIncome.payableToYear.toString()
    );
    formData.append(
      "DoesAnnualIncomeIncrease",
      otherIncome.amountWillIncrease.toString()
    );

    return axios.post(resource, formData);
  }

  public async deletePensionPot(id: number): Promise<AxiosResponse> {
    const resource = this.buildDeleteResource("definedcontribution", id);
    return axios.delete(resource);
  }

  public async deleteFinalSalary(id: number): Promise<AxiosResponse> {
    const resource = this.buildDeleteResource("finalsalary", id);
    return axios.delete(resource);
  }

  public async deleteOtherSavings(id: number): Promise<AxiosResponse> {
    const resource = this.buildDeleteResource("othersavings", id);
    return axios.delete(resource);
  }

  public async deleteOtherCash(id: number): Promise<AxiosResponse> {
    const resource = this.buildDeleteResource("otherinvestments", id);
    return axios.delete(resource);
  }

  public async deleteOtherIncome(id: number): Promise<AxiosResponse> {
    const resource = this.buildDeleteResource("otherincome", id);
    return axios.delete(resource);
  }

  public async checkIfCanSolveByTakingLess(
    scenarioType: ScenarioType
  ): Promise<TakeLessResponse> {
    const resource = this.getSolveResource("takeless", scenarioType);
    const response = await axios.get(resource);
    return response.data as TakeLessResponse;
  }

  public async solveByTakingLess(reduceByPercent: number): Promise<boolean> {
    const payload = { reduceByPercent };
    return await axios.post("/assumptions/reduceincome", payload);
  }

  public async checkIfCanSolveByPayingMore(
    scenarioType: ScenarioType
  ): Promise<PayMoreResponse> {
    const resource = this.getSolveResource("paymore", scenarioType);
    const response = await axios.get(resource);
    return response.data as PayMoreResponse;
  }

  public async solveByIncreasingPayments(
    monthlyPayment: number
  ): Promise<boolean> {
    const payload = { monthlyPayment };
    return await axios.post("/assumptions/increasepayments", payload);
  }

  public async checkIfCanSolveByEquityRelease(scenarioType: ScenarioType) {
    const resource = this.getSolveResource("equityrelease", scenarioType);
    const response = await axios.get(resource);
    return response.data as EquityReleaseResponse;
  }

  public async solveByEquityRelease(amount: number) {
    const payload = { amount };
    return await axios.post("/assumptions/releaseequity", payload);
  }

  public async findAccounts(filter: string): Promise<AccountSearchResponse> {
    try {
      const response = await axios.get(`/account/find?filter=${filter}`);
      const accounts = response.data.accounts;
      const recordCount = response.data.recordCount;
      return new AccountSearchResponse(accounts, recordCount);
    } catch (error) {
      return new AccountSearchResponse(new Array<AccountSearchResponseItem>(), 0);
    }
  }

  public async canBuyGuaranteedIncome(): Promise<
    CanBuyGuaranteedIncomeResponse
  > {
    try {
      const response = await axios.get("design/guaranteedincome");
      return response.data as CanBuyGuaranteedIncomeResponse;
    } catch (error) {
      return {
        isViable: false,
        percentAllocatedFromPots: 0,
        status: error.response?.status,
        message: error.response?.data.message
      } as CanBuyGuaranteedIncomeResponse;
    }
  }

  public async applyGuaranteedIncome(
    ageAtSubscription: number | null,
    percentageToAllocate: number | null
  ): Promise<SubscribeToGuaranteedIncomeResponse> {
    let response: Promise<SubscribeToGuaranteedIncomeResponse>;

    try {
      const { data } = await axios.post<SubscribeToGuaranteedIncomeResponse>(
        "design/guaranteedincome",
        { ageAtSubscription, percentageToAllocate }
      );
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";

        response = Promise.resolve<SubscribeToGuaranteedIncomeResponse>(
          new SubscribeToGuaranteedIncomeResponse(status, message)
        );
      } else {
        response = Promise.resolve<SubscribeToGuaranteedIncomeResponse>(
          new SubscribeToGuaranteedIncomeResponse(
            HttpStatusCode.InternalServerError,
            "An unexpected error occurred."
          )
        );
      }
    }

    return response;
  }

  public async updateGuaranteedIncome(
    ageAtSubscription: number,
    percentageToAllocate: number
  ): Promise<SubscribeToGuaranteedIncomeResponse> {
    let response: Promise<SubscribeToGuaranteedIncomeResponse>;

    try {
      const { data } = await axios.patch<SubscribeToGuaranteedIncomeResponse>(
        "design/guaranteedincome",
        { ageAtSubscription, percentageToAllocate }
      );
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";

        response = Promise.resolve<SubscribeToGuaranteedIncomeResponse>(
          new SubscribeToGuaranteedIncomeResponse(status, message)
        );
      } else {
        response = Promise.resolve<SubscribeToGuaranteedIncomeResponse>(
          new SubscribeToGuaranteedIncomeResponse(
            HttpStatusCode.InternalServerError,
            "An unexpected error occurred."
          )
        );
      }
    }

    return response;
  }

  public async deleteGuaranteedIncome(): Promise<void> {
    return await axios.delete("/design/guaranteedincome");
  }

  public async setGuaranteedIncomeNotificationStatus(notify: boolean) {
    return await axios.patch("/design/guaranteedincomenotification", { notify });
  }

  public async updateProtectedPot(protectedPot: number): Promise<void> {
    const payload = { protectedPot };
    return await axios.post("/design/protectedpot", payload);
  }

  public async updatePensionReturnRate(rateOfReturn: number): Promise<void> {
    const payload = { rateOfReturn };
    return await axios.post("/design/pensionsreturn", payload);
  }

  public async updateSavingsReturnRate(rateOfReturn: number): Promise<void> {
    const payload = { rateOfReturn };
    return await axios.post("/design/savingsreturn", payload);
  }

  public async updateHealthState(healthState: HealthState): Promise<void> {
    const payload = { healthState };
    return await axios.post("/lifeexpectancy/health", payload);
  }

  public async updateSurvivabilityState(): Promise<void> {
    return await axios.post("/lifeexpectancy/survival");
  }

  public async getYearsToLiveFromRetirementOn50thSurvivalPercentile(): Promise<number> {
    const response = await axios.get("/lifeexpectancy/survival/average");
    return response.data;
  }

  public async registerEmail(email: string, phoneNumber: string): Promise<MessageResponse> {
    try {
      const payload = { email, phoneNumber };
      const { data } = await axios.post("/account/register", payload);
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      const messageResponse = this.getMessageResponseFromError(error);
      return Promise.resolve<MessageResponse>(messageResponse)
    }
  }

  public async login(email: string, password: string): Promise<MessageResponse> {
    try {
      const payload = { Email: email, Password: password };
      await axios.post("/account/login", payload);
      return new MessageResponse(HttpStatusCode.Ok, "");
    } catch (error) {
      const status: number|null = error.response?.status;

      if (status == null) {
        const messageResponse = this.getMessageResponseFromError(error);
        return Promise.resolve<MessageResponse>(messageResponse)
      }

      if (status == HttpStatusCode.Unauthorised) {
        return Promise.resolve<MessageResponse>(new MessageResponse(status, "The email or password is incorrect."));
      } else if (status == HttpStatusCode.Locked) {
        return Promise.resolve<MessageResponse>(new MessageResponse(status, "Your account has been temporarily locked due to multiple failed login attempts."));
      } else {
        return Promise.resolve<MessageResponse>(new MessageResponse(status, "An error occurred when processing your login attempt."));
      }
    }
  }

  public async finalSalarySchemeCodeLogin(code: string): Promise<RedirectResponse> {
    try {
      const payload = { asid: code };
      var response = await axios.post("/login/finalsalaryschemecodelogin", payload);
      const redirectUrl = response.data.redirectUrl;
      return new RedirectResponse(HttpStatusCode.Ok, "", redirectUrl);
    } catch (error) {
      const status: number | null = error.response?.status;

      if (status == null) {
        const messageResponse = this.getMessageResponseFromError(error);
        return Promise.resolve<RedirectResponse>(new RedirectResponse(messageResponse.status, messageResponse.message, ""));
      }

      const message = error.response?.data?.message ?? "An error occurred when processing your request.";
      return Promise.resolve<RedirectResponse>(new RedirectResponse(status, message, ""));
    }
  }

  public async sendPasswordResetLink(email: string): Promise<MessageResponse> {
    try {
      var response = await axios.post("/account/forgotpassword", { email });
      const message = response.data.message;
      return new MessageResponse(HttpStatusCode.Ok, message);
    } catch (error) {
      const status: number|null = error.response?.status;

      if (status == null) {
        const messageResponse = this.getMessageResponseFromError(error);
        return Promise.resolve<MessageResponse>(messageResponse);
      }

      const message = error.response?.data?.message ?? "An error occurred when processing your request.";
      return Promise.resolve<MessageResponse>(new MessageResponse(status, message));
    }
  }

  public async resetPassword(password: string, email: string, token: string, isAccountConfirmation: boolean): Promise<MessageResponse> {
    try {
      const resource = isAccountConfirmation
        ? "/account/confirmemail"
        : "/account/changeforgottenpassword";
      var response = await axios.post(resource, { password, email, token });
      const message = response.data.message;
      return new MessageResponse(HttpStatusCode.Ok, message);
    } catch (error) {
      const status: number|null = error.response?.status;

      if (status == null) {
        const messageResponse = this.getMessageResponseFromError(error);
        return Promise.resolve<MessageResponse>(messageResponse)
      }

      const message = error.response?.data.message ?? "An error occurred when processing your request.";
      return Promise.resolve<MessageResponse>(new MessageResponse(status, message));
    }
  }

  public async getEquityReleaseRate(
    age: number
  ): Promise<EquityReleaseRateResponse> {
    let response: Promise<EquityReleaseRateResponse>;

    try {
      const { data } = await axios.get<EquityReleaseRateResponse>(
        `countries/gb/equityrelease/onefamily/${age}`
      );
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";

        response = Promise.resolve<EquityReleaseRateResponse>(
          new EquityReleaseRateResponse(status, message)
        );
      } else {
        response = Promise.resolve<EquityReleaseRateResponse>(
          new EquityReleaseRateResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred.")
        );
      }
    }

    return response;
  }

  public async getPensionProviders(): Promise<PensionProvidersResponse> {
    let response: Promise<PensionProvidersResponse>;

    try {
      const { data } = await axios.get<PensionProvidersResponse>(`countries/gb/providers/pensions`);
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";
        response = Promise.resolve<PensionProvidersResponse>(
          new PensionProvidersResponse(status, message)
        );
      } else {
        response = Promise.resolve<PensionProvidersResponse>(
          new PensionProvidersResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred.")
        );
      }
    }

    return response;
  }

  public async getAnnuityProviders(): Promise<AnnuityProvidersResponse> {
    let response: Promise<AnnuityProvidersResponse>;

    try {
      const { data } = await axios.get<AnnuityProvidersResponse>(`countries/gb/providers/annuities`);
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";
        response = Promise.resolve<AnnuityProvidersResponse>(
          new AnnuityProvidersResponse(status, message)
        );
      } else {
        response = Promise.resolve<AnnuityProvidersResponse>(
          new AnnuityProvidersResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred.")
        );
      }
    }

    return response;
  }

  public async getAdvisers(): Promise<AdvisersResponse> {
    let response: Promise<AdvisersResponse>;

    try {
      const { data } = await axios.get<AdvisersResponse>(`countries/gb/advisers`);
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";
        response = Promise.resolve<AdvisersResponse>(
          new AdvisersResponse(status, message)
        );
      } else {
        response = Promise.resolve<AdvisersResponse>(
          new AdvisersResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred.")
        );
      }
    }

    return response;
  }

  public async beginPensionTransfer(applicationInfo: ApplicationInfo): Promise<BeginPensionTransferResponse> {
    let response: Promise<BeginPensionTransferResponse>;

    function createAllocationPayload(fundAllocation: FundAllocation): { id: number, name: string, percentage: number, amount: number } {
      return {
        id: fundAllocation.fund.id,
        name: fundAllocation.fund.name,
        percentage: fundAllocation.percentage,
        amount: fundAllocation.amount
      };
    }

    const allocationsAtPresent =  applicationInfo.fundAllocCalcAtPresent.allocations.map(createAllocationPayload);
    const allocationsAtRetirement =  applicationInfo.fundAllocCalcAtRetirement.allocations.map(createAllocationPayload);

    try {
      const payload = {
        pensionPotIds: applicationInfo.pensionPotIds,
        matchedYears: applicationInfo.matchedYears,
        matchedYearsInWindow: applicationInfo.matchedYearsInWindow,
        taxFreeLumpSumFundId: applicationInfo.taxFreeLumpSumFundId,
        lumpSumAmount: applicationInfo.fundAllocCalcAtPresent.moneyFund.amount,
        allocationsAtPresent: allocationsAtPresent,
        allocationsAtRetirement: allocationsAtRetirement
      };

      const { data } = await axios.post<BeginPensionTransferResponse>(`countries/gb/providers/pl/transfer`, payload);

      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";

        response = Promise.resolve<BeginPensionTransferResponse>(
          new BeginPensionTransferResponse(status, message)
        );
      } else {
        response = Promise.resolve<BeginPensionTransferResponse>(
          new BeginPensionTransferResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred.")
        );
      }
    }

    return response;
  }

  public async grantConsent(permittedEntityType: PermittedEntityType, granteeId: ExternalAgency, consentType: ConsentType): Promise<MessageResponse> {
    try {
      const payload = {
        permittedEntityTypeId: permittedEntityType,
        permittedEntityId: granteeId,
        consentTypeId: consentType
      };
      const { data } = await axios.post("/consents", payload);
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";
        return Promise.resolve<MessageResponse>(new MessageResponse(status, message));
      } else {
        return Promise.resolve<MessageResponse>(new MessageResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred."));
      }
    }
  }

  public async revokeConsent(permittedEntityType: PermittedEntityType, granteeId: ExternalAgency, consentType: ConsentType): Promise<MessageResponse> {
    try {
      const queryString = `?permittedEntityTypeId=${permittedEntityType}&permittedEntityId=${granteeId}&consentTypeId=${consentType}`;
      const { data } = await axios.delete("/consents" + queryString);
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";
        return Promise.resolve<MessageResponse>(new MessageResponse(status, message));
      } else {
        return Promise.resolve<MessageResponse>(new MessageResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred."));
      }
    }
  }

  public async updateAdditionalPersonalInfo(additionalInfo: AdditionalPersonalInfo): Promise<MessageResponse> {
    try {
      const { data } = await axios.post("/personaldetails/additionalinfo", additionalInfo);
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";
        return Promise.resolve<MessageResponse>(new MessageResponse(status, message));
      } else {
        return Promise.resolve<MessageResponse>(new MessageResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred."));
      }
    }
  }

  public async updateFinalSalaryOption(finalSalaryOption: FinalSalaryOption, taxFreeCashLumpSumAmount: number, immediateSpendAmount: number): Promise<MessageResponse> {
    try {
      const payload = {
        finalSalaryOption,
        taxFreeCashLumpSumAmount,
        immediateSpendAmount
      };
      const { data } = await axios.post("/dboptions", payload);
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";
        return Promise.resolve<MessageResponse>(new MessageResponse(status, message));
      } else {
        return Promise.resolve<MessageResponse>(new MessageResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred."));
      }
    }
  }

  public async getCountries(): Promise<DataResult<Array<Country>>> {
    try {
      const { data } = await axios.get("countries");
      return new DataResult<Array<Country>>(true, "", data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message: string = error.response?.data.message ?? "Error unknown";
        return Promise.resolve<DataResult<Array<Country>>>(new DataResult<Array<Country>>(false, message, null));
      } else {
        return Promise.resolve<DataResult<Array<Country>>>(new DataResult<Array<Country>>(false, "An unexpected error occurred.", null));
      }
    }
  }

  public async resetCrossover(): Promise<MessageResponse> {
    try {
      const { data } = await axios.patch("/settings/resetcrossover");
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      const messageResponse = this.getMessageResponseFromError(error);
      return Promise.resolve<MessageResponse>(messageResponse);
    }
  }

  public async getNotifications(notificationConcern: NotificationConcern): Promise<DataResult<Array<AppNotification>>> {
    try {
      const path = `/notifications?concern=${notificationConcern}`;
      const { data } = await axios.get(path);
      return  new DataResult<Array<AppNotification>>(true, "", data.notifications);
    } catch (error) {
      let message = "An unexpected error occurred.";
      if (axios.isAxiosError(error)) {
        message = error.response?.data.message ?? "Error unknown.";
      }
      return Promise.resolve<DataResult<Array<AppNotification>>>(new DataResult<Array<AppNotification>>(false, message, null));
    }
  }

  public async getFinalSalaryOptions(schemeId: number): Promise<DataResult<FinalSalarySchemeOptionsDetailResponse>> {
    try {
      const { data } = await axios.get(`/finalsalary/schemes/${schemeId}/options`);
      return new DataResult<FinalSalarySchemeOptionsDetailResponse>(true, "", data);
    } catch (error) {
      let message = "An unexpected error occurred.";
      if (axios.isAxiosError(error)) {
        message = error.response?.data.message ?? "Error unknown.";
      }
      return Promise.resolve<DataResult<FinalSalarySchemeOptionsDetailResponse>>(new DataResult<FinalSalarySchemeOptionsDetailResponse>(false, message, null));
    }
  }
  
  public async requestFinancialAdvice(name: string, email: string, phoneNo: string, ifaId: number): Promise<MessageResponse> {
    try {
      const payload = {
        name: name,
        email: email,
        phoneNumber: phoneNo,
        ifaId: ifaId
      };
      const { data } = await axios.post("/dboptions/requestadvice", payload);
      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";
        return Promise.resolve<MessageResponse>(new MessageResponse(status, message));
      } else {
        return Promise.resolve<MessageResponse>(new MessageResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred."));
      }
    }
  }

  public async requestIFAContact(ifaName: string, requestorName: string, phoneNumber: string): Promise<BeginPensionTransferResponse> {
    let response: Promise<BeginPensionTransferResponse>;

    try {
      const payload = {
        requestorName,
        phoneNumber
      };

      const { data } = await axios.post<BeginPensionTransferResponse>(`countries/gb/advisers/${encodeURIComponent(ifaName)}/contact`, payload);

      data.status = HttpStatusCode.Ok;
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status: number = error.response?.status ?? 500;
        const message: string = error.response?.data.message ?? "Error unknown";

        response = Promise.resolve<BeginPensionTransferResponse>(
          new BeginPensionTransferResponse(status, message)
        );
      } else {
        response = Promise.resolve<BeginPensionTransferResponse>(
          new BeginPensionTransferResponse(HttpStatusCode.InternalServerError, "An unexpected error occurred.")
        );
      }
    }

    return response;
  }

  private buildResource(entityType: string, id: number): string {
    const action = id === Constants.NewId ? "create" : "edit";
    const resource = `/${entityType}/${action}`;
    return resource;
  }

  private buildDeleteResource(entityType: string, id: number): string {
    return `/${entityType}/delete?id=${id}`;
  }

  private getSolveResource(
    solveType: string,
    scenarioType: ScenarioType
  ) {
    const scenarioName = ScenarioInfo.getScenarioServerName(scenarioType);
    return `retiree/{retireeId}/plan/solve/${solveType}?scenarioName=${scenarioName}`;
  }

  private getMessageResponseFromError(error): MessageResponse {
    if (axios.isAxiosError(error)) {
      const status: number = error.response?.status ?? 400;
      const message: string = error.response?.data.message ?? "";
      return new MessageResponse(status, message);
    } else {
      return new MessageResponse(
        HttpStatusCode.InternalServerError,
        "An unexpected error occurred."
      );
    }
  }
}

const api = new API();

export default api;

